import { useState, useCallback } from "react"
import { useProviderFinderClient } from "#/pages/provider-finder/hooks/useProviderFinderClient"

export interface GooglePlacesV1Response {
  suggestions: {
    placePrediction: {
      place: string
      placeId: string
      text: {
        text: string
        matches: Array<{ endOffset: number; startOffset?: number }>
      }
      structuredFormat: {
        mainText: {
          text: string
          matches: Array<{ endOffset: number; startOffset?: number }>
        }
        secondaryText: {
          text: string
        }
      }
      types: string[]
    }
  }[]
}

export interface PlacePrediction {
  place_id: string
  description: string
  matched_substrings: Array<{ offset: number; length: number }>
  structured_formatting: {
    main_text: string
    main_text_matched_substrings: Array<{ offset: number; length: number }>
    secondary_text: string
  }
  terms: Array<{ offset: number; value: string }>
  types: string[]
}

interface UsePlacesAutocompleteResult {
  placePredictions: PlacePrediction[]
  isPlacePredictionsLoading: boolean
  getPlacePredictions: (input: string) => Promise<void>
}

function transformV1Response(data: GooglePlacesV1Response): PlacePrediction[] {
  return (data?.suggestions || []).map((suggestion) => {
    const pred = suggestion.placePrediction
    const text = pred.text?.text || ""
    const matches = pred.text?.matches || []
    const structured = pred.structuredFormat

    return {
      place_id: pred.placeId || "",
      description: text,
      matched_substrings: matches.map((m) => ({
        offset: m.startOffset || 0,
        length: (m.endOffset || 0) - (m.startOffset || 0)
      })),
      structured_formatting: {
        main_text: structured?.mainText?.text || "",
        main_text_matched_substrings: (structured?.mainText?.matches || []).map((m) => ({
          offset: m.startOffset || 0,
          length: (m.endOffset || 0) - (m.startOffset || 0)
        })),
        secondary_text: structured?.secondaryText?.text || ""
      },
      terms: text.split(",").reduce((acc: Array<{ offset: number; value: string }>, term, index, arr) => {
        const trimmed = term.trim()
        if (trimmed) {
          const previousTerms = arr.slice(0, index).join(",")
          let offset = previousTerms.length

          if (index > 0) {
            offset += index
          }

          const leadingSpaces = term.length - term.trimStart().length
          offset += leadingSpaces

          acc.push({ offset, value: trimmed })
        }
        return acc
      }, []),
      types: pred.types || []
    }
  })
}

export function usePlacesAutocomplete(language: string): UsePlacesAutocompleteResult {
  const [placePredictions, setPlacePredictions] = useState<PlacePrediction[]>([])
  const [isPlacePredictionsLoading, setIsPlacePredictionsLoading] = useState(false)
  const providerFinderClient = useProviderFinderClient()

  const getPlacePredictions = useCallback(
    async (input: string) => {
      if (!input) {
        setPlacePredictions([])
        return
      }

      setIsPlacePredictionsLoading(true)
      try {
        const response = await providerFinderClient.getPlacePredictions(input, language)
        const predictions = transformV1Response(response)
        setPlacePredictions(predictions)
      } catch (err) {
        setPlacePredictions([])
      } finally {
        setIsPlacePredictionsLoading(false)
      }
    },
    [language, providerFinderClient]
  )

  return { placePredictions, isPlacePredictionsLoading, getPlacePredictions }
}
